import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { DateRange } from 'react-day-picker';
import { containerVariants } from '@/components/animations';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { SiteHeader } from '@/components/site-header';
import { CalendarDatePicker } from '@/components/calendar-date-picker';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

// Import billing components
import { OverviewMetrics } from './components/OverviewMetrics';
import { AssistantsRanking } from './components/AssistantsRanking';
import { TrendsChart } from './components/TrendsChart';
import { HourlyUsageChart } from './components/HourlyUsageChart';
import { DailyUsageChart } from './components/DailyUsageChart';
import { ReportGenerator } from './components/ReportGenerator';
import { MessagingOverview } from './components/MessagingOverview';

// Import billing API
import {
  getBillingOverview,
  getBillingTrends,
  getUsagePatterns,
  getMessagingOverview
} from '@/api/billing/billing.api';
import {
  BillingOverview,
  BillingTrends,
  UsagePatterns as UsagePatternsType,
  MessagingOverview as MessagingOverviewType
} from '@/api/billing/billing.types';
import { formatDateForAPI } from '@/api/billing/billing.utils';
import { AlertTriangle } from 'lucide-react';

const Billing = () => {
  const { toast } = useEnhancedToast();

  // Filter states
  const [date, setDate] = useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });
  const [trendsMonths, setTrendsMonths] = useState<number>(6);
  
  // Data states
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [trends, setTrends] = useState<BillingTrends | null>(null);
  const [patterns, setPatterns] = useState<UsagePatternsType | null>(null);
  const [messagingOverview, setMessagingOverview] = useState<MessagingOverviewType | null>(null);

  // Loading states
  const [loadingOverview, setLoadingOverview] = useState<boolean>(false);
  const [loadingTrends, setLoadingTrends] = useState<boolean>(false);
  const [loadingPatterns, setLoadingPatterns] = useState<boolean>(false);
  const [loadingMessaging, setLoadingMessaging] = useState<boolean>(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);
  
  // Refs to access current values in stable callbacks
  const dateRef = useRef(date);
  const trendsMonthsRef = useRef(trendsMonths);
  
  // Update refs when state changes
  useEffect(() => {
    dateRef.current = date;
  }, [date]);
  
  useEffect(() => {
    trendsMonthsRef.current = trendsMonths;
  }, [trendsMonths]);

  const loadOverviewData = useCallback(async () => {
    setLoadingOverview(true);
    setError(null);
    try {
      const currentDate = dateRef.current;
      const params = {
        startDate: currentDate?.from ? formatDateForAPI(currentDate.from) : undefined,
        endDate: currentDate?.to ? formatDateForAPI(currentDate.to) : undefined,
      };
      
      const result = await getBillingOverview(params);
      
      if ('error' in result) {
        throw new Error(result.error);
      } else if (result.status === 'success' && result.data) {
        setOverview(result.data);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load billing overview';
      setError(message);
      console.error('Error loading overview:', err);
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const loadTrendsData = useCallback(async () => {
    setLoadingTrends(true);
    try {
      const currentTrendsMonths = trendsMonthsRef.current;
      const result = await getBillingTrends({ months: currentTrendsMonths });
      
      if ('error' in result) {
        throw new Error(result.error);
      } else if (result.status === 'success' && result.data) {
        setTrends(result.data);
      }
    } catch (err) {
      console.error('Error loading trends:', err);
    } finally {
      setLoadingTrends(false);
    }
  }, []);

  const loadPatternsData = useCallback(async () => {
    setLoadingPatterns(true);
    try {
      const currentDate = dateRef.current;
      const params = {
        startDate: currentDate?.from ? formatDateForAPI(currentDate.from) : undefined,
        endDate: currentDate?.to ? formatDateForAPI(currentDate.to) : undefined,
      };
      
      const result = await getUsagePatterns(params);
      
      if ('error' in result) {
        throw new Error(result.error);
      } else if (result.status === 'success' && result.data) {
        setPatterns(result.data);
      }
    } catch (err) {
      console.error('Error loading patterns:', err);
    } finally {
      setLoadingPatterns(false);
    }
  }, []);

  const loadMessagingData = useCallback(async () => {
    setLoadingMessaging(true);
    try {
      const currentDate = dateRef.current;
      const params = {
        startDate: currentDate?.from ? formatDateForAPI(currentDate.from) : undefined,
        endDate: currentDate?.to ? formatDateForAPI(currentDate.to) : undefined,
      };

      const result = await getMessagingOverview(params);

      if ('error' in result) {
        throw new Error(result.error);
      } else if (result.status === 'success' && result.data) {
        setMessagingOverview(result.data);
      }
    } catch (err) {
      console.error('Error loading messaging overview:', err);
    } finally {
      setLoadingMessaging(false);
    }
  }, []);

  const handleDateSelect = useCallback((range: { from: Date | undefined; to: Date | undefined }) => {
    setDate(range);
  }, []);

  const handleTrendsMonthsChange = useCallback((months: number) => {
    setTrendsMonths(months);
  }, []);

  // Load data when filters change (but not if API is down)
  useEffect(() => {
    if (date?.from && date?.to) {
      loadOverviewData();
      loadPatternsData();
      loadMessagingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date?.from, date?.to]);

  useEffect(() => {
    loadTrendsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsMonths]);

  const handleChartError = (chartName: string) => () => {
    toast({
      title: `${chartName} Error`,
      description: `Failed to load ${chartName.toLowerCase()}. Please try again later.`,
      variant: 'error',
      icon: AlertTriangle
    });
  };

  const handleRefresh = useCallback(() => {
    if (date?.from && date?.to) {
      loadOverviewData();
      loadPatternsData();
      loadMessagingData();
    }
    loadTrendsData();
  }, [date, loadOverviewData, loadPatternsData, loadMessagingData, loadTrendsData]);

  const isRefreshing = loadingOverview || loadingTrends || loadingPatterns || loadingMessaging;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader
        title="Billing & Usage"
        subtitle="Monitor usage costs and performance analytics"
      >
        {/* Date Range Picker */}
        <CalendarDatePicker
          date={date}
          onDateSelect={handleDateSelect}
          numberOfMonths={2}
        />

        {/* Refresh Button */}
        <Button
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="gap-2"
        >
          <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
          Refresh
        </Button>
      </SiteHeader>

      <div className="flex-1 overflow-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="px-4 py-6 md:px-8 md:py-8 space-y-6"
        >
          {error && (
            <div 
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 border border-red-300 dark:border-red-800"
              role="alert"
            >
              <span className="font-medium">Error!</span> {error}
            </div>
          )}

          {/* First Row: Overview Metrics */}
          <OverviewMetrics 
            overview={overview} 
            isLoading={loadingOverview} 
            onError={handleChartError('Overview Metrics')}
          />

          {/* Second Row: Report Generator - Main Action Card */}
          <ReportGenerator onError={handleChartError('Report Generator')} />

          {/* Third Row: Main Charts Grid */}
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
            <AssistantsRanking
              overview={overview}
              isLoading={loadingOverview}
              onError={handleChartError('Assistants Ranking')}
              className="h-[400px]"
            />

            <MessagingOverview
              messagingOverview={messagingOverview}
              isLoading={loadingMessaging}
              onError={handleChartError('Messaging Overview')}
              className="h-[400px]"
            />
          </div>

          {/* Fourth Row: Trends Chart */}
          <TrendsChart
            trends={trends}
            isLoading={loadingTrends}
            onError={handleChartError('Trends Chart')}
            className="h-[400px]"
            trendsMonths={trendsMonths}
            onTrendsMonthsChange={handleTrendsMonthsChange}
          />

          {/* Fifth Row: Usage Pattern Charts */}
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
            <HourlyUsageChart
              patterns={patterns}
              isLoading={loadingPatterns}
              onError={handleChartError('Hourly Usage')}
              className="h-[400px]"
            />
            
            <DailyUsageChart
              patterns={patterns}
              isLoading={loadingPatterns}
              onError={handleChartError('Daily Activity')}
              className="h-[400px]"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Billing;